"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useCallback, useMemo } from "react";

import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";

import { Category } from "../../services/get-category-data";
import { useUserBehavior } from "../../services/get-user-behavior";
import { useUpdateUserBehavior } from "../../services/update-user-behavior";

/* eslint-disable react-hooks/exhaustive-deps */

export const useFavoriteMedia = ({ mediaId }: { mediaId: string }) => {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const { lang } = useLang();
  const { isUnauthenticated } = useAuth();
  const category = pathname.split("/")[4] as Category;
  const { mutate: updateUserBehavior } = useUpdateUserBehavior();
  const { data, isLoading } = useUserBehavior({ category });
  const isFavorite = useMemo(
    () => !!data?.[mediaId]?.favorite,
    [data, mediaId]
  );

  const toggleFavorite = useCallback(() => {
    if (isUnauthenticated || isLoading || !data || !category || !mediaId)
      return;

    updateUserBehavior(
      {
        category,
        mediaId,
        data: { favorite: !isFavorite },
      },
      {
        onSuccess: (_, variables) => {
          queryClient.invalidateQueries({
            queryKey: ["user-behavior", lang, variables.category],
          });
        },
      }
    );
  }, [
    isUnauthenticated,
    isLoading,
    data,
    category,
    mediaId,
    updateUserBehavior,
  ]);

  return { isFavorite, toggleFavorite };
};
